import { memo, useMemo, useRef, useState } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useHasWIVOrEIVAccess } from '@frontend/insurance-verification';
import { titleCase } from '@frontend/string';
import { theme } from '@frontend/theme';
import { Chip, SpinningLoader, useModalControl, useTooltip } from '@frontend/design-system';
import { useOpenInsuranceReport } from '../hooks/use-open-insurance-report';
import { useGetInsuranceStatusesStoreShallow } from '../stores/use-insurance-status-store';
import { insuranceStatusChipTrackingIds } from '../tracking-ids';
import { INSURANCE_STATUS_MAPPING } from '../utils/constants';
import { InsuranceInlineReport } from './insurance-inline-report';
import { VerifyInsuranceFormSidePanelContainer } from './verify-insurance-side-panel/verify-insurance-form-side-panel-container';

type ScheduleAppointmentCardInsuranceStatusChipProps = {
  calendarDate: string;
  locationId: string;
  sourceId: string;
  pmsID?: string;
  personInfo: {
    personId: string;
    personName: string;
  };
};

export const ScheduleAppointmentCardInsuranceStatusChip = memo(
  ({ personInfo, locationId, sourceId, pmsID, calendarDate }: ScheduleAppointmentCardInsuranceStatusChipProps) => {
    const { t } = useTranslation('insuranceDetails');
    const [isReportOpen, setIsReportOpen] = useState(false);
    const { personId } = personInfo;
    const ref = useRef<HTMLDivElement>(null);
    const { tooltipProps, triggerProps, Tooltip } = useTooltip({});
    const verifyInsuranceModalControl = useModalControl({
      disableReturnFocus: true,
    });
    const verificationMetricModalControl = useModalControl({
      disableReturnFocus: true,
    });

    const { insuranceStatuses } = useGetInsuranceStatusesStoreShallow('insuranceStatuses');
    const { isDental } = useHasWIVOrEIVAccess();

    const { insuranceStatus, verificationPartner } = useMemo(() => {
      const insuranceStatusByPersonId =
        insuranceStatuses?.[calendarDate]?.locationVerificationCounts?.[locationId]?.coverageStatusByPersonId?.[
          personId
        ] || '';

      const partnerDetails =
        insuranceStatuses?.[calendarDate]?.locationVerificationCounts?.[locationId]?.partnerDetails?.[personId] || '';

      return { insuranceStatus: insuranceStatusByPersonId, verificationPartner: partnerDetails };
    }, [insuranceStatuses, calendarDate, locationId, personId]);

    const statusInfo = INSURANCE_STATUS_MAPPING.get(insuranceStatus ? insuranceStatus.toLowerCase() : 'unknown');
    const shouldShowTuuthFairyReport = verificationPartner?.toLowerCase() === 'tuuthfairy' && isDental;

    const { handleOpenInsuranceReport, isLoading: isLoadingInsuranceReport } = useOpenInsuranceReport({
      personLocationId: locationId,
      personID: personId,
      personSourceId: sourceId,
      personPMSID: pmsID || '',
      shouldShowTuuthFairyReport,
    });

    const isVerified = useMemo(
      () => insuranceStatus?.toLowerCase() === 'active' || insuranceStatus?.toLowerCase() === 'verified',
      [insuranceStatus]
    );

    const handleInsuranceStatusClick = (e: React.MouseEvent<HTMLDivElement>) => {
      e?.stopPropagation();
      if (isVerified) {
        shouldShowTuuthFairyReport ? setIsReportOpen(true) : handleOpenInsuranceReport();
      } else {
        verifyInsuranceModalControl.openModal();
      }
    };

    const handleStopPropagationOnClick = (e: React.MouseEvent) => {
      e?.stopPropagation();
    };

    if (isLoadingInsuranceReport) {
      return <SpinningLoader size='xxs' />;
    }

    return (
      <>
        <div
          data-hide-calendar-tooltip='true'
          {...triggerProps}
          css={insuranceStatusChipStyle}
          onClick={(e) => handleInsuranceStatusClick(e)}
          data-trackingid={insuranceStatusChipTrackingIds.insuranceStatusChipContainer}
        >
          <Chip.Tag
            ref={ref}
            css={{ padding: theme.spacing(0.5), cursor: 'pointer', height: 16, width: 20, gap: 0, border: `1px solid` }}
            variant={statusInfo?.variant || 'default'}
            data-trackingid={insuranceStatusChipTrackingIds.insuranceStatusChipTag}
          >
            <Icon
              name={statusInfo?.iconName || 'insurance-incomplete-small'}
              css={{ color: 'currentColor', fontSize: '1.5rem' }}
            />
          </Chip.Tag>
        </div>
        <Tooltip {...tooltipProps}>{titleCase(insuranceStatus || t('Unverified'))}</Tooltip>
        <div onClick={(e) => handleStopPropagationOnClick(e)}>
          {verifyInsuranceModalControl.modalProps.show && (
            <VerifyInsuranceFormSidePanelContainer
              verifyInsuranceModalControl={verifyInsuranceModalControl}
              verificationMetricModalControl={verificationMetricModalControl}
              locationId={locationId}
              personId={personId}
              shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
              hasVerificationError={insuranceStatus?.toLowerCase() === 'error'}
            />
          )}
          <InsuranceInlineReport
            locationId={locationId}
            sourceId={sourceId}
            personInfo={personInfo}
            personPMSId={pmsID}
            shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
            isReportOpen={isReportOpen}
            setIsReportOpen={setIsReportOpen}
          />
        </div>
      </>
    );
  }
);

ScheduleAppointmentCardInsuranceStatusChip.displayName = 'ScheduleAppointmentCardInsuranceStatusChip';

const insuranceStatusChipStyle: Interpolation<Theme> = {
  width: 'fit-content',
  '&:hover > div': {
    outline: `2px solid ${theme.colors.primary50}`,
    outlineOffset: 1,
  },
};
